import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, Wrench, Car,
  Settings, PanelLeftClose, PanelLeft, Moon, Sun, Package, Shield,
  LogOut, User, ChevronDown, Building2
} from 'lucide-react'
import { cn } from '../../lib/utils'
import { useUIStore } from '../../stores/ui'
import { useThemeStore } from '../../stores/theme'
import { useAuthStore } from '../../stores/auth'
import { IconButton } from '../ui/IconButton'
import { Separator } from '../ui/Separator'
import { Avatar, AvatarFallback, getInitials } from '../ui/Avatar'
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/Tooltip'

const primaryNav = [
  { name: 'Dashboard', icon: LayoutDashboard, href: '/dashboard' },
  { name: 'Jobs', icon: Wrench, href: '/jobs' },
  { name: 'Vehicle History', icon: Car, href: '/vehicles' },
]

const secondaryNav = [
  { name: 'Catalog', icon: Package, href: '/catalog' },
  { name: 'Settings', icon: Settings, href: '/settings' },
]

export function Sidebar() {
  const location = useLocation()
  const navigate = useNavigate()
  const collapsed = useUIStore((s) => s.sidebarCollapsed)
  const toggle = useUIStore((s) => s.toggleSidebar)
  const isDark = useThemeStore((s) => s.resolvedTheme) === 'dark'
  const setTheme = useThemeStore((s) => s.setTheme)
  
  // Auth store
  const user = useAuthStore((s) => s.user)
  const shops = useAuthStore((s) => s.shops)
  const currentShopId = useAuthStore((s) => s.currentShopId)
  const setCurrentShop = useAuthStore((s) => s.setCurrentShop)
  const logout = useAuthStore((s) => s.logout)

  const currentShop = shops.find((s) => s.id === currentShopId)
  const shopName = currentShop?.name || 'Select Shop'
  const canAccessAdminConsole = Boolean(user?.isShopbayAdmin)
  const userName = user?.name || user?.email || 'User'
  const userInitials = getInitials(userName)
  const internalNav = canAccessAdminConsole
    ? [{ name: 'Admin Console', icon: Shield, href: '/admin' }]
    : []

  const isActive = (href: string) =>
    href === '/dashboard' ? location.pathname === '/dashboard' : location.pathname.startsWith(href)

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const NavLink = ({ name, icon: Icon, href }: { name: string; icon: typeof LayoutDashboard; href: string }) => {
    const active = isActive(href)
    const link = (
      <Link
        to={href}
        className={cn(
          'flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors relative',
          active
            ? 'bg-primary-600/20 text-primary-300 before:absolute before:left-1 before:top-1.5 before:bottom-1.5 before:w-[2px] before:bg-primary-500 before:rounded-full shadow-inner shadow-black/20'
            : 'text-content-secondary hover:bg-tint/5 hover:text-content-primary',
          collapsed && 'justify-center px-2'
        )}
      >
        <Icon className="h-5 w-5 shrink-0" />
        {!collapsed && <span>{name}</span>}
      </Link>
    )

    if (collapsed) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right">{name}</TooltipContent>
        </Tooltip>
      )
    }
    return link
  }

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-sticky hidden md:flex flex-col h-screen border-r border-border bg-surface-primary/95 backdrop-blur-md transition-[width] duration-normal',
        collapsed ? 'w-sidebar-sm' : 'w-sidebar'
      )}
    >
      {/* Shop header with switcher */}
      <div className={cn('flex items-center h-nav px-3 border-b border-border', collapsed ? 'justify-center' : 'justify-between')}>
        {!collapsed && shops.length > 1 ? (
          <div className="relative group">
            <button className="flex items-center gap-2 text-sm font-bold tracking-[0.14em] text-primary-500 uppercase truncate hover:opacity-80 transition-opacity">
              <Building2 className="h-4 w-4" />
              <span className="truncate max-w-[140px]">{shopName}</span>
              <ChevronDown className="h-3 w-3" />
            </button>
            {/* Shop switcher dropdown */}
            <div className="absolute top-full left-0 mt-1 w-56 bg-surface-primary border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
              <div className="py-2">
                <p className="px-3 py-1 text-xs text-content-tertiary uppercase tracking-wider">Switch Shop</p>
                {shops.map((shop) => (
                  <button
                    key={shop.id}
                    onClick={() => setCurrentShop(shop.id)}
                    className={cn(
                      'w-full px-3 py-2 text-left text-sm hover:bg-tint/5 transition-colors',
                      shop.id === currentShopId ? 'text-primary-300 bg-primary-600/10' : 'text-content-secondary'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span className="truncate">{shop.name}</span>
                      <span className="text-xs text-content-tertiary capitalize">{shop.role}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : !collapsed ? (
          <span className="text-sm font-bold tracking-[0.14em] text-primary-500 uppercase truncate">
            {shopName}
          </span>
        ) : null}
        <IconButton onClick={toggle} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          {collapsed ? <PanelLeft /> : <PanelLeftClose />}
        </IconButton>
      </div>

      {/* Primary nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
        {primaryNav.map((item) => <NavLink key={item.name} {...item} />)}
        <Separator className="my-3" />
        {secondaryNav.map((item) => <NavLink key={item.name} {...item} />)}
        {internalNav.length > 0 && (
          <>
            <Separator className="my-3" />
            {internalNav.map((item) => <NavLink key={item.name} {...item} />)}
          </>
        )}
      </nav>

      {/* User section */}
      <div className={cn('border-t border-border px-3 py-3', collapsed && 'flex flex-col items-center gap-2')}>
        {!collapsed ? (
          <div className="space-y-2">
            {/* User menu */}
            <div className="relative group">
              <button className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-tint/5 transition-colors">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="text-xs">{userInitials}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium text-content-primary truncate">{userName}</p>
                  <p className="text-xs text-content-tertiary truncate">{user?.email}</p>
                </div>
                <ChevronDown className="h-4 w-4 text-content-tertiary" />
              </button>
              
              {/* User dropdown */}
              <div className="absolute bottom-full left-0 mb-1 w-full bg-surface-primary border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <div className="py-1">
                  <Link
                    to="/profile"
                    className="flex items-center gap-2 px-3 py-2 text-sm text-content-secondary hover:bg-tint/5 hover:text-content-primary transition-colors"
                  >
                    <User className="h-4 w-4" />
                    Profile
                  </Link>
                  <button
                    onClick={() => setTheme(isDark ? 'light' : 'dark')}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-content-secondary hover:bg-tint/5 hover:text-content-primary transition-colors"
                  >
                    {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    {isDark ? 'Light Mode' : 'Dark Mode'}
                  </button>
                  <Separator className="my-1" />
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-danger-500 hover:bg-danger-500/10 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/profile">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="text-xs">{userInitials}</AvatarFallback>
                  </Avatar>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">Profile</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton onClick={() => setTheme(isDark ? 'light' : 'dark')} size="sm">
                  {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </IconButton>
              </TooltipTrigger>
              <TooltipContent side="right">{isDark ? 'Light Mode' : 'Dark Mode'}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton onClick={handleLogout} size="sm" className="text-danger-500">
                  <LogOut className="h-4 w-4" />
                </IconButton>
              </TooltipTrigger>
              <TooltipContent side="right">Logout</TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
    </aside>
  )
}
